import React from "react";
import { Box, Skeleton, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import { OfferDetailsResponse } from "../../../../explorer/types/Basket/OfferDetailsResponse";
import { OfferDetailPart } from "../../../../explorer/types/Basket/OfferDetailPart";
import { OrderDetailPart } from "../../../../explorer/types/Basket/OrderDetailPart";
import { useTranslation } from "../../../../explorer/hooks/useTranslation";
import { OrderDetailsResponse } from "../../../../explorer/types/Basket/OrderDetailsResponse";
import useLanguages from "../../../../explorer/hooks/useLanguages";
import PartList, { ColumnAlign, ColumnWidth } from "../../PartsList/PartList";
import DateComponent from "../upload/DateComponent";
import useBrands from "../../../../explorer/hooks/useBrands";
import PartBasicInfoComponent from "../upload/PartBasicInfoComponent";
import OfferOrderQuantityComponent from "../upload/OfferOrderQuantityComponent";
import MoreOrderInfoComponent from "../upload/MoreOrderInfoComponent";
import {
    PriceContainer,
    PropertiesContainer,
    useClasses,
} from "./OfferOrderDetailsPage.style";
import DetailsGrid from "./DetailsGrid";
import SkeletonList from "../../../pages/SpareParts/HomePage/SkeletonList/SkeletonList";
import BasketTotalPrice from "../../../pages/BasketPage/BasketTotalPrice";
import PriceFormatComponent from "../../PartsList/TableLayout/TableColumnComponents/PriceFormatComponent";
import OfferOrderNoteIconComponent from "../../PartsList/TableLayout/TableColumnComponents/OfferOrderNoteIconComponent";
import DiscountComponent from "../../PartsList/TableLayout/TableColumnComponents/DiscountComponent";
import LeadTimeComponent from "../upload/LeadTimeComponent";
import { formatCurrency } from "../../../../explorer/helpers/CommonHelper";
import PartMccLogoComponent from "../../PartsList/TableLayout/TableColumnComponents/PartMccLogoComponent";

interface OfferOrderDetailsPageProps {
    details: OfferDetailsResponse | OrderDetailsResponse | undefined;
    parts: OfferDetailPart[] | OrderDetailPart[] | undefined;
    loading: boolean;
}

const OfferOrderDetailsPage: React.FC<OfferOrderDetailsPageProps> = ({
    details,
    parts,
    loading,
}) => {
    const { t } = useTranslation();
    const lang = useLanguages().currentLanguage;
    const currentBrand = useBrands().currentBrand;

    const isSingleManufacturer = () => {
        return currentBrand?.manufacturers.length === 1;
    };

    const isOffer = (
        details: OfferDetailsResponse | OrderDetailsResponse | undefined
    ): details is OfferDetailsResponse => {
        return (details as OfferDetailsResponse)?.offerCode !== undefined;
    };

    const hasDiscounts = parts?.some((part) => part.discountPercentage > 0);

    const classes = useClasses();

    const getColumns = (
        value: OfferDetailsResponse | OrderDetailsResponse | undefined
    ) =>
        isOffer(value)
            ? [
                  ...(isSingleManufacturer()
                      ? []
                      : [
                            {
                                name: "manufacturerCompany",
                                viewHeader: false,
                                width: ColumnWidth.IconWidth,
                                customComponent: PartMccLogoComponent,
                                align: ColumnAlign.Left,
                            },
                        ]),
                  {
                      name: "codeDescription",
                      viewHeader: true,
                      customComponent: PartBasicInfoComponent,
                      align: ColumnAlign.Left,
                  },
                  {
                      name: "note",
                      viewHeader: true,
                      additionalProps: false,
                      align: ColumnAlign.Left,
                      customComponent: OfferOrderNoteIconComponent,
                  },
                  {
                      name: "qty",
                      viewHeader: true,
                      customComponent: OfferOrderQuantityComponent,
                      align: ColumnAlign.Left,
                  },
                  {
                      name: "availabilityDate",
                      viewHeader: true,
                      customComponent: DateComponent,
                      align: ColumnAlign.Left,
                  },
                  {
                      name: "leadTime",
                      viewHeader: true,
                      align: ColumnAlign.Left,
                      width: ColumnWidth.IconWidth,
                      customComponent: LeadTimeComponent,
                  },
                  {
                      name: "grossUnitPrice",
                      viewHeader: true,
                      customComponent: PriceFormatComponent,
                      align: ColumnAlign.Right,
                  },
                  {
                      name: "netTotalPrice",
                      viewHeader: true,
                      customComponent: PriceFormatComponent,
                      align: ColumnAlign.Right,
                  },
                  ...(hasDiscounts ? [{
                    name: "discountPercentage",
                    viewHeader: true,
                    align: ColumnAlign.Center,
                    customComponent: DiscountComponent,
                  }]: []),
              ]
            : [
                  ...(isSingleManufacturer()
                      ? []
                      : [
                            {
                                name: "manufacturerCompany",
                                align: ColumnAlign.Left,
                                viewHeader: false,
                                width: ColumnWidth.IconWidth,
                                customComponent: PartMccLogoComponent,
                            },
                        ]),
                  {
                      name: "codeDescription",
                      viewHeader: true,
                      customComponent: PartBasicInfoComponent,
                      align: ColumnAlign.Left,
                  },
                  {
                      name: "note",
                      viewHeader: true,
                      additionalProps: false,
                      align: ColumnAlign.Left,
                      customComponent: OfferOrderNoteIconComponent,
                  },
                  {
                      name: "qty",
                      viewHeader: true,
                      customComponent: OfferOrderQuantityComponent,
                      align: ColumnAlign.Left,
                  },
                  {
                      name: "deliveredQuantity",
                      viewHeader: true,
                      align: ColumnAlign.Center,
                  },
                  {
                      name: "grossUnitPrice",
                      viewHeader: true,
                      customComponent: PriceFormatComponent,
                      align: ColumnAlign.Right,
                  },
                  {
                      name: "netTotalPrice",
                      viewHeader: true,
                      customComponent: PriceFormatComponent,
                      align: ColumnAlign.Right,
                  },
                  ...(hasDiscounts ? [{
                      name: "discountPercentage",
                      viewHeader: true,
                      align: ColumnAlign.Center,
                      customComponent: DiscountComponent,
                    }]: []),
                  {
                      name: "3dots",
                      viewHeader: false,
                      customComponent: MoreOrderInfoComponent,
                      align: ColumnAlign.Right,
                      width: ColumnWidth.IconWidth,
                  },
              ];

    return (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            {loading ? (
                <>
                    <SkeletonList
                        mkey="OfferOrderDetails"
                        hasHeader
                        cols={3}
                        rows={5}
                        rowsForCell={1}
                        hasLabel
                    />
                    <Skeleton width={"100%"} height={100} />
                    <SkeletonList
                        mkey="OfferOrderDetailsTable"
                        hasHeader
                        rows={5}
                        cols={7}
                        useIcons
                        iconCols={1}
                        rowsForCell={1}
                    />
                </>
            ) : (
                <>
                    <Box
                        sx={{
                            display: "grid",
                            gridTemplateColumns: "repeat(4, 1fr)",
                            // gap: 2,
                        }}
                    >
                        {details ? (
                            <PropertiesContainer sx={{ gridColumn: "span 4" }}>
                                <DetailsGrid
                                    details={details}
                                    type={isOffer(details) ? "offer" : "order"}
                                />{" "}
                            </PropertiesContainer>
                        ) : (
                            <Typography variant="body1">
                                {t("common.noDetailsAvailable")}
                            </Typography>
                        )}
                    </Box>
                    <PriceContainer></PriceContainer>
                    <PartList
                        items={parts || []}
                        loading={loading}
                        columns={getColumns(details)}
                    />
                    <BasketTotalPrice
                        currency={details?.currency}
                        className={classes.priceBlock}
                        fullTotal={details?.grossTotalAmount ?? 0}
                        netTotal={details?.netTotalAmount ?? 0}
                        discount={details?.discountAmount ?? 0}
                        hidePrice={false}
                        showDiscount={
                            details?.discountAmount !== undefined &&
                            details?.discountAmount > 0
                        }
                    />
                </>
            )}
        </Box>
    );
};

export default OfferOrderDetailsPage;
